// src/pages/Gallery.jsx
import React, { useState } from 'react';
import { 
  Box, Button, Typography, Grid, Card,Chip, 
  IconButton, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, MenuItem, FormControl,
  InputLabel, Select, ImageList, ImageListItem, 
  ImageListItemBar, Checkbox, FormControlLabel
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const initialImages = [
  { 
    id: 1, 
    title: 'Food Drive 2023', 
    caption: 'Our annual food distribution event',
    event: 'Food Drive',
    date: '2023-05-15',
    isPublic: true,
    url: '/gallery/food-drive-1.jpg'
  },
  { 
    id: 2, 
    title: 'Education Camp', 
    caption: 'Children learning at our education camp',
    event: 'Education Camp',
    date: '2023-06-20',
    isPublic: true,
    url: '/gallery/education-camp-1.jpg'
  },
  { 
    id: 3, 
    title: 'Tree Plantation', 
    caption: 'Volunteers planting trees',
    event: 'Tree Plantation',
    date: '2023-07-10',
    isPublic: true,
    url: '/gallery/tree-plant-1.jpg'
  },
  { 
    id: 4, 
    title: 'Medical Camp', 
    caption: 'Free health checkup camp',
    event: 'Medical Camp',
    date: '2023-08-05',
    isPublic: false,
    url: '/gallery/medical-camp-1.jpg'
  },
];

const Gallery = () => {
  const [images, setImages] = useState(initialImages);
  const [editingImage, setEditingImage] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [filterEvent, setFilterEvent] = useState('all');
  const [filterPublic, setFilterPublic] = useState('all');

  const handleEditImage = (image) => {
    setEditingImage(image);
    setOpenDialog(true);
  };

  const handleSaveImage = () => {
    if (editingImage) {
      setImages(prev => 
        prev.map(img => 
          img.id === editingImage.id ? editingImage : img
        )
      );
      setOpenDialog(false);
      setEditingImage(null);
    }
  };

  const handleCancelEdit = () => {
    setOpenDialog(false);
    setEditingImage(null);
  };

  const handleOpenDeleteDialog = (image) => {
    setImageToDelete(image);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setImageToDelete(null);
  };

  const handleDeleteImage = () => {
    if (imageToDelete) {
      setImages(prev => prev.filter(img => img.id !== imageToDelete.id));
      handleCloseDeleteDialog();
    }
  };

  const handleAddImage = () => {
    setEditingImage({
      id: images.length + 1,
      title: '',
      caption: '',
      event: '',
      date: new Date().toISOString().split('T')[0],
      isPublic: true,
      url: ''
    });
    setOpenDialog(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingImage(prev => ({ ...prev, [name]: value }));
  };

  const handleTogglePublic = (e) => {
    setEditingImage(prev => ({ ...prev, isPublic: e.target.checked }));
  };

  const handleSelectImage = (id) => {
    setSelectedImages(prev => 
      prev.includes(id) 
        ? prev.filter(imgId => imgId !== id) 
        : [...prev, id]
    );
  };

  const handleDeleteSelected = () => {
    setImages(prev => prev.filter(img => !selectedImages.includes(img.id)));
    setSelectedImages([]);
  };

  const handleUpload = () => {
    setUploading(true);
    // Simulate upload
    setTimeout(() => {
      alert('Images uploaded successfully!');
      setUploading(false);
    }, 2000);
  };

  const filteredImages = images.filter(image => {
    return (
      (filterEvent !== 'all' ? image.event === filterEvent : true) &&
      (filterPublic !== 'all' ? image.isPublic === (filterPublic === 'public') : true)
    );
  });

  const uniqueEvents = [...new Set(images.map(img => img.event))];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>Gallery</Typography>
      
      <Card sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Event</InputLabel>
              <Select
                value={filterEvent}
                onChange={(e) => setFilterEvent(e.target.value)}
                label="Filter by Event"
              >
                <MenuItem value="all">All Events</MenuItem>
                {uniqueEvents.map(event => (
                  <MenuItem key={event} value={event}>{event}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Visibility</InputLabel>
              <Select
                value={filterPublic}
                onChange={(e) => setFilterPublic(e.target.value)}
                label="Filter by Visibility"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              startIcon={<CloudUploadIcon />}
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Images'}
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />}
              onClick={handleAddImage}
            >
              Add Image
            </Button>
          </Grid>
        </Grid>
      </Card>
      
      {selectedImages.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography>
            {selectedImages.length} image(s) selected
          </Typography>
          <Button 
            variant="outlined" 
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteSelected}
          >
            Delete Selected
          </Button>
        </Box>
      )}
      
      <Card>
        <ImageList cols={3} gap={16} variant="masonry">
          {filteredImages.map((image) => (
            <ImageListItem key={image.id} sx={{ overflow: 'hidden', borderRadius: 1 }}>
              <Box sx={{ position: 'relative' }}>
                <img
                  src={image.url || '/placeholder.jpg'}
                  alt={image.title}
                  loading="lazy"
                  style={{ 
                    height: 200, 
                    width: '100%', 
                    objectFit: 'cover',
                    filter: selectedImages.includes(image.id) ? 'opacity(0.7)' : 'none'
                  }}
                />
                <Checkbox
                  checked={selectedImages.includes(image.id)}
                  onChange={() => handleSelectImage(image.id)}
                  icon={<span style={{ visibility: 'hidden' }} />}
                  checkedIcon={<CheckIcon sx={{ 
                    color: 'white', 
                    backgroundColor: 'primary.main',
                    borderRadius: '50%',
                    padding: '2px'
                  }} />}
                  sx={{ 
                    position: 'absolute', 
                    top: 8, 
                    left: 8,
                    backgroundColor: selectedImages.includes(image.id) ? 'rgba(0,0,0,0.2)' : 'transparent',
                    borderRadius: '50%'
                  }}
                />
                {!image.isPublic && (
                  <Chip 
                    label="Private" 
                    size="small" 
                    color="warning"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  />
                )}
                <ImageListItemBar
                  title={image.title}
                  subtitle={image.caption}
                  actionIcon={
                    <Box>
                      <IconButton
                        sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                        onClick={() => handleEditImage(image)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                        onClick={() => handleOpenDeleteDialog(image)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                />
              </Box>
            </ImageListItem>
          ))}
        </ImageList>
      </Card>

      {/* Image Detail Dialog */}
      <Dialog open={openDialog} onClose={handleCancelEdit} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingImage?.id > images.length ? 'Add New Image' : 'Edit Image Details'}
        </DialogTitle>
        <DialogContent dividers>
          {editingImage && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={editingImage.title}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Caption"
                  name="caption"
                  value={editingImage.caption}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Associated Event"
                  name="event"
                  value={editingImage.event}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date"
                  name="date"
                  value={editingImage.date}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Image URL"
                  name="url"
                  value={editingImage.url}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editingImage.isPublic}
                      onChange={handleTogglePublic}
                      color="primary"
                    />
                  }
                  label="Publicly Visible"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveImage} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this image?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteImage} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Gallery;